/**
 * Screen-space clustering for the live canvas's personnel markers.
 *
 * WHY: zoom/pan fixes crowding for an operator who is DRIVING the dashboard.
 * It does nothing for the default view, and this canvas is also an unattended
 * wall display (PublicLiveDashboard rotates sites every ~20s with nobody
 * touching it). At fit-zoom on a 400x250m site in a ~713px canvas the scale is
 * ~1.8 px per metre, so a 20px worker icon covers ~11m of ground: two workers
 * standing a realistic 10m apart overlap almost entirely and whichever renders
 * last swallows the clicks.
 *
 * EVERYTHING HERE IS IN SCREEN PIXELS, against points already projected
 * through the current viewport (zoom included). That is what makes the two
 * phases compose rather than fight: zooming in multiplies the pixel gap
 * between two workers, so clusters split apart on their own with no extra
 * zoom-aware rule. Nothing in this file knows about metres, zoom or pan.
 *
 * SAFETY OVERRIDE (the requirement that outranks every other one here): a
 * worker in `sos` or `warning` is NEVER clusterable -- see `isAlarming`. Such
 * a worker always renders as their own individual marker with their own
 * pulse/colour, so an alarm can never be reduced to "+3" on someone else's
 * badge. A cluster additionally carries the WORST status among its members, so
 * even if the status vocabulary grows an alarming value this file has not been
 * taught about, the colour still escalates instead of silently reading normal.
 *
 * STABILITY: personnel poll every 2s and the mock demo workers move
 * continuously, so a single threshold makes markers pop in and out of clusters
 * on every poll -- on a wall display flicker is worse than crowding. Hysteresis
 * fixes that: points MERGE at `mergeRadius` but an existing cluster only SPLITS
 * once a member drifts past the wider `splitRadius`, so a worker hovering at
 * the boundary stays where they were instead of oscillating. The caller feeds
 * the previous call's output back in as `previousGroups`.
 */

// 24px: two 20px icons closer than this are visually one blob. At fit-zoom on
// the 400x250m site above that is ~13m of ground, i.e. it catches exactly the
// "two workers 10m apart" case that motivated this.
export const MERGE_RADIUS_PX = 24;
// 40px (~1.7x merge): the width of the hysteresis band. A clustered worker has
// to walk ~9m further than the distance that merged them before the cluster
// gives them up.
export const SPLIT_RADIUS_PX = 40;

// Higher = worse. Drives both the safety override and a cluster's colour.
// `offline` outranks `active` so a cluster containing a worker who has stopped
// reporting does not read as entirely healthy.
const SEVERITY = { active: 0, offline: 1, warning: 2, sos: 3 };

const severityOf = (status) => SEVERITY[status] ?? 0;

/**
 * The safety override. `sos`/`warning` personnel are excluded from clustering
 * entirely rather than being folded in and recoloured, because an alarming
 * worker needs their own position, their own SOS pulse and their own click
 * target -- a count badge gives an operator none of those.
 */
export const isAlarming = (status) => severityOf(status) >= SEVERITY.warning;

export const worstStatus = (statuses) =>
  statuses.reduce(
    (worst, status) =>
      severityOf(status) > severityOf(worst) ? status : worst,
    "active",
  );

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Distance to the NEAREST member of a group. Both the merge and the split test
// are expressed against this same quantity, which is what makes the two radii
// directly comparable: a pair merges at 24px apart and gives up at 40px apart.
// (Measuring the split from the group's centroid instead would silently double
// the band for a pair, since each of the two sits half the gap from it.)
const distanceToNearest = (point, members) =>
  members.reduce(
    (best, member) =>
      member === point ? best : Math.min(best, distance(point, member)),
    Infinity,
  );

const centroidOf = (points) => ({
  x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
  y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
});

const compareIds = (a, b) => String(a).localeCompare(String(b));

const toGroup = (members) => {
  const ids = members.map((m) => m.id).sort(compareIds);
  const centre = centroidOf(members);
  return {
    // Keyed by the lowest member id so a stable cluster keeps its React key
    // (and therefore its DOM node and any open popover) across polls.
    key: `cluster-${ids[0]}`,
    x: centre.x,
    y: centre.y,
    count: members.length,
    status: worstStatus(members.map((m) => m.status)),
    members,
  };
};

/**
 * @param {object} params
 * @param {Array<{id:*, x:number, y:number, status:string}>} params.points
 *   personnel already projected to canvas pixels. Extra fields are carried
 *   through untouched on `group.members`, which is how the caller gets the
 *   original person object back.
 * @param {Array} [params.previousGroups] the previous return value of this
 *   function. Supplies the hysteresis; omit it and you get plain thresholding.
 * @returns {Array<{key:string, x:number, y:number, count:number,
 *   status:string, members:Array}>} one group per rendered marker; `count: 1`
 *   groups are ordinary single markers. Ordered worst-status-LAST so an
 *   alarming worker paints on top of the pack rather than under it.
 */
export const clusterPersonnel = ({
  points = [],
  previousGroups = [],
  mergeRadius = MERGE_RADIUS_PX,
  splitRadius = SPLIT_RADIUS_PX,
}) => {
  const alarming = points.filter((p) => isAlarming(p.status));
  const clusterable = points
    .filter((p) => !isAlarming(p.status))
    .sort((a, b) => compareIds(a.id, b.id));

  const byId = new Map(clusterable.map((p) => [p.id, p]));
  const claimed = new Set();

  // 1. Carry forward last poll's clusters, dropping only members that have
  //    drifted past the WIDER split radius. This is the whole anti-flicker
  //    mechanism: membership is sticky inside the band.
  const carried = [];
  previousGroups.forEach((group) => {
    const survivors = (group?.members ?? [])
      .map((member) => byId.get(member.id))
      .filter((point) => point && !claimed.has(point.id));
    if (survivors.length < 2) return;

    const kept = survivors.filter(
      (p) => distanceToNearest(p, survivors) <= splitRadius,
    );
    if (kept.length < 2) return;

    kept.forEach((p) => claimed.add(p.id));
    carried.push(kept);
  });

  // 2. Points not carried join a surviving cluster if they are within the
  //    (narrow) merge radius of it, so a worker walking into a crowd is
  //    absorbed rather than starting a competing cluster on top of it.
  const carriedAtStart = carried.map((members) => members);
  const leftovers = [];
  clusterable.forEach((point) => {
    if (claimed.has(point.id)) return;
    let best = -1;
    let bestDistance = Infinity;
    carriedAtStart.forEach((members, index) => {
      const d = distanceToNearest(point, members);
      if (d <= mergeRadius && d < bestDistance) {
        best = index;
        bestDistance = d;
      }
    });
    if (best >= 0) {
      carried[best] = [...carried[best], point];
    } else {
      leftovers.push(point);
    }
  });

  // 3. Everything still loose: one greedy pass in id order. N is a site's
  //    headcount (tens), so O(n^2) is the right amount of machinery.
  const seeded = new Set();
  const fresh = [];
  leftovers.forEach((seed) => {
    if (seeded.has(seed.id)) return;
    seeded.add(seed.id);
    const members = [seed];
    leftovers.forEach((other) => {
      if (seeded.has(other.id)) return;
      if (distance(seed, other) <= mergeRadius) {
        seeded.add(other.id);
        members.push(other);
      }
    });
    fresh.push(members);
  });

  const groups = [
    ...carried.map(toGroup),
    ...fresh.map(toGroup),
    ...alarming.map((point) => toGroup([point])),
  ];

  return groups.sort((a, b) => severityOf(a.status) - severityOf(b.status));
};
